import { ChevronDown, ChevronRight } from "@gravity-ui/icons";
import { Checkbox, Icon } from "@gravity-ui/uikit";
import cn from "classnames";
import { memo, MouseEvent, useCallback, useMemo, useState } from "react";
import { formatCount } from "src/util";

import { SelectOptionTree } from "../../types";
import { compareByProductCount } from "../../utils";
import css from "./OptionsTree.module.scss";

export interface DisableCondition {
  disableCondition?: (option: SelectOptionTree) => boolean;
}

interface Option<Value> {
  label: string;
  value: Value;
}

interface OptionsTreeProps extends DisableCondition {
  option: SelectOptionTree;
  onSelect: (node: Option<number>) => void;
  level: number;
  expandAll?: boolean;
  isDisabled?: boolean;
  selectedValues?: Set<number>;
}

export const OptionsTree = memo(
  ({
    option,
    onSelect,
    level,
    expandAll,
    disableCondition,
    selectedValues,
  }: OptionsTreeProps) => {
    const [expand, setExpand] = useState(!!expandAll);

    // expand — локальный toggle (onSwitchExpand), но проп expandAll должен его
    // перекрывать при изменении.
    const [prevExpandAll, setPrevExpandAll] = useState(expandAll);
    if (expandAll !== prevExpandAll) {
      setPrevExpandAll(expandAll);
      setExpand(!!expandAll);
    }

    const isDisabled = disableCondition ? disableCondition(option) : false;
    const isSelected = selectedValues?.has(option.value) ?? false;
    const isExpandable = Boolean(option.options?.length);

    const onToggleSelect = useCallback(() => {
      if (!isDisabled) onSelect({ label: option.label, value: option.value });
    }, [isDisabled, onSelect, option.label, option.value]);

    const onSwitchExpand = useCallback(() => {
      setExpand((prev) => !prev);
    }, []);

    // Выбор любой категории — через чекбокс (мультиселект не закрывает меню).
    // Клик по строке только раскрывает ветку; лист, у которого раскрывать
    // нечего, выбираем по клику.
    const onRowClick = useCallback(() => {
      if (isExpandable) {
        onSwitchExpand();
      } else if (!isDisabled) {
        onToggleSelect();
      }
    }, [isExpandable, isDisabled, onSwitchExpand, onToggleSelect]);

    const stopPropagation = useCallback((e: MouseEvent) => {
      e.stopPropagation();
    }, []);

    const sortOptions = useMemo(
      () => option.options?.slice().sort(compareByProductCount),
      [option.options],
    );

    const levelClass =
      level === 0 ? css.levelRoot : level === 1 ? css.levelMid : css.levelDeep;

    return (
      <div className={css.node}>
        <div
          className={cn(css.row, levelClass, {
            [css.disabled]: isDisabled,
            [css.selected]: isSelected,
          })}
          onClick={onRowClick}
        >
          <span className={css.toggle}>
            {isExpandable ? (
              <Icon
                className={css.chevron}
                data={expand ? ChevronDown : ChevronRight}
                size={16}
              />
            ) : (
              <span className={css.leafDot} />
            )}
          </span>

          <span className={css.checkboxWrap} onClick={stopPropagation}>
            <Checkbox
              size="m"
              checked={isSelected}
              disabled={isDisabled}
              onUpdate={onToggleSelect}
            />
          </span>

          <div className={css.main}>
            <span className={css.label}>{option.label}</span>
            {option.productCount !== undefined && (
              <span className={css.count}>
                {formatCount(option.productCount)}
              </span>
            )}
          </div>
        </div>

        {expand && Boolean(sortOptions?.length) && (
          <div className={css.children}>
            {sortOptions?.map((item) => (
              <OptionsTree
                option={item}
                onSelect={onSelect}
                level={level + 1}
                key={item.value}
                expandAll={expandAll}
                disableCondition={disableCondition}
                selectedValues={selectedValues}
              />
            ))}
          </div>
        )}
      </div>
    );
  },
);
OptionsTree.displayName = "OptionsTree";
